import React, { useState } from 'react';
import { Fan, Home, HardHat, Trees, Droplets, Smile, Sparkles, Trash2, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { IllustrativeReviews } from './IllustrativeReviews';

// Add only verified, client-approved testimonials: { id, industry, name, company, quote, system }
export const TESTIMONIALS = [];

const IND = { hvac: ['HVAC', Fan], roofing: ['Roofing', Home], construction: ['Construction', HardHat], landscaping: ['Landscaping', Trees], plumbing: ['Plumbing', Droplets], dental: ['Dental', Smile], medspa: ['Med Spas', Sparkles], junk: ['Junk Removal', Trash2] };

const EXAMPLES = [
  { id: 'hvac', title: 'Missed-Call Recovery During Peak Season', body: 'Calls that arrive while technicians are on jobs receive an approved text-back, and replies are logged so dispatch can schedule the visit.', system: 'Lead Recovery System' },
  { id: 'roofing', title: 'Structured Estimate Follow-Up', body: 'Delivered estimates are tracked in the pipeline, with scheduled check-ins and estimator alerts when homeowners reply.', system: 'Booking Growth System' },
  { id: 'construction', title: 'New Lead Qualification', body: 'Project inquiries are captured with scope and timeline, then routed to the right estimator to request a site visit.', system: 'Booking Growth System' },
  { id: 'landscaping', title: 'Seasonal Customer Reactivation', body: 'Eligible past customers are invited back before each season, and responses update the CRM for the office to confirm.', system: 'Booking Growth System' },
  { id: 'plumbing', title: 'Urgent Appointment Requests', body: 'Text and web inquiries receive an instant response, urgency details are saved, and the office is alerted to confirm a window.', system: 'Lead Recovery System' },
  { id: 'dental', title: 'Cancellation & No-Show Rebooking', body: 'Missed visits trigger an approved rebooking message, and the front desk confirms the new time patients request.', system: 'Booking Growth System' },
  { id: 'medspa', title: 'Consultation Inquiry Follow-Up', body: 'Consultation requests receive timely follow-up and reminders, keeping every inquiry organized in one pipeline.', system: 'Booking Growth System' },
  { id: 'junk', title: 'Estimate Details & Pickup Scheduling', body: 'Follow-up collects photos and ZIP codes for estimates, then guides customers to request a pickup window.', system: 'Lead Recovery System' },
];

const Filters = ({ sel, setSel }) => (
  <div className="yv-chiptabs" role="tablist" aria-label="Filter by industry">
    {[['all', 'All Industries'], ...Object.entries(IND).map(([k, [l]]) => [k, l])].map(([k, l]) => (
      <button key={k} role="tab" aria-selected={sel === k} className={sel === k ? 'on' : ''} onClick={() => setSel(k)} data-testid={`trust-filter-${k}`}>{l}</button>
    ))}
  </div>
);

export const TestimonialCard = ({ t }) => {
  const [label, Icon] = IND[t.industry];
  return (
    <figure className="yv-tm-card" data-testid={`testimonial-${t.id}`}>
      <Quote className="w-7 h-7 yv-tm-quote" aria-hidden="true" />
      <blockquote>{t.quote}</blockquote>
      <figcaption><Icon className="w-4 h-4" aria-hidden="true" /><span><b>{t.name}</b>{[t.company, label].filter(Boolean).join(' · ')}</span><span className="yv-tm-sys yv-mono">{t.system}</span></figcaption>
    </figure>
  );
};

const ExampleCard = ({ x }) => {
  const [label, Icon] = IND[x.id];
  return (
    <article className="yv-tm-card is-example" data-testid={`trust-example-${x.id}`}>
      <div className="yv-tm-top"><span className="yv-sol-icon"><Icon className="w-5 h-5" aria-hidden="true" /></span><span className="yv-tag yv-tag-dark">Illustrative Example</span></div>
      <span className="yv-mono yv-tm-ind">{label}</span>
      <h3 className="yv-sol-title">{x.title}</h3>
      <p>{x.body}</p>
      <span className="yv-tm-sys yv-mono">{x.system}</span>
    </article>
  );
};

const Carousel = ({ items }) => {
  const [i, setI] = useState(0);
  const n = items.length;
  const t = items[((i % n) + n) % n];
  return (
    <div className="yv-tm-carousel" data-testid="testimonial-carousel">
      <TestimonialCard t={t} key={t.id} />
      {n > 1 && <div className="yv-hd-ctrl"><button className="yv-hd-btn" onClick={() => setI(i - 1)} aria-label="Previous testimonial" data-testid="testimonial-prev"><ChevronLeft className="w-4 h-4" /></button><button className="yv-hd-btn" onClick={() => setI(i + 1)} aria-label="Next testimonial" data-testid="testimonial-next"><ChevronRight className="w-4 h-4" /></button></div>}
    </div>
  );
};

export const IndustryTrust = () => {
  const [sel, setSel] = useState('all');
  const verified = TESTIMONIALS.length > 0;
  if (!verified) return <IllustrativeReviews />;
  const items = verified ? TESTIMONIALS.filter((t) => sel === 'all' || t.industry === sel) : EXAMPLES.filter((x) => sel === 'all' || x.id === sel);
  return (
    <section id="trust" className="yv-section yv-dark yv-grid-dark" data-testid="trust-section">
      <div className="yv-container">
        <div className="yv-section-head yv-split-head">
          <div><p className="yv-eyebrow">{verified ? 'Client Experiences' : 'Industry Examples'}</p>
            <h2 className="yv-h2" data-testid="trust-heading">{verified ? <>Built For Different Industries. <span className="yv-blue-ink yv-block">Trusted For Better Operations.</span></> : <>Designed For The Way <span className="yv-blue-ink yv-block">Service Businesses Operate.</span></>}</h2></div>
          <p className="yv-lede">{verified ? 'Discover How Business Owners Experience More Connected Communication, Organized Follow-Up, And Streamlined Operations With YOVAI.' : 'Illustrative Scenarios Showing How YOVAI Systems Can Support Different Operations. These Are Examples, Not Customer Success Stories.'}</p>
        </div>
        <Filters sel={sel} setSel={setSel} />
        {verified ? (items.length ? <Carousel items={items} key={sel} /> : <p className="yv-lede mt-8">No Client Experiences Shared For This Industry Yet.</p>) : (
          <div className="yv-tm-grid" key={sel}>{items.map((x) => <ExampleCard key={x.id} x={x} />)}</div>
        )}
      </div>
    </section>
  );
};
